import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  deleteField,
  serverTimestamp,
  Timestamp,
  FieldValue,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { Staff, StaffEmploymentInfo, WorkScheduleDay, ScheduleException } from '../models/StaffModels';
import { useAuth } from './AuthContext';
import PINHashingService from '../services/PINHashingService';

const StaffContext = createContext();

export const useStaff = () => {
  return useContext(StaffContext);
};

export const StaffProvider = ({ children }) => {
  // Fix: Extract currentCompany instead of companyId directly
  // NOTE: currentUser is required for archivedBy on archive/reactivate.
  // Confirm this matches the actual property name exposed by AuthContext.
  const { currentCompany, currentUser } = useAuth();
  const companyId = currentCompany?.id;
  
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [departmentsLoaded, setDepartmentsLoaded] = useState(false);
  
  // Enhanced loading states for better UX
  const [operationLoading, setOperationLoading] = useState({
    updating: false,
    deleting: false,
    creating: false,
    clockingOut: false,
    updatingTimeEntry: false,
    archiving: false
  });

  // Derived lists - filtered in memory, never via a Firestore query, since
  // documents that predate the `active` field have no `active` key at all
  // and would be excluded by a `where('active', '==', true)` query.
  const activeStaff = staff.filter(s => s.active !== false);
  const archivedStaff = staff.filter(s => s.active === false);
  
  // Debug current company info
  useEffect(() => {
    console.log("StaffContext - Company Info:", { currentCompany, companyId });
  }, [currentCompany, companyId]);
  
  useEffect(() => {
    if (companyId) {
      fetchDepartments().then(() => {
        fetchAllStaff();
      });
    } else {
      setDepartments([]);
      setStaff([]);
      setLoading(false);
      setDepartmentsLoaded(false);
    }
  }, [companyId]);

  // Enhanced error handling helper
  const handleError = (error, operation, context = '') => {
    const errorMessage = `Failed to ${operation}${context ? ` ${context}` : ''}`;
    console.error(`StaffContext Error (${operation}):`, error);
    setError(errorMessage);
    return errorMessage;
  };

  // Clear error helper
  const clearError = () => {
    setError(null);
  };

  // Fetch all departments for the company
  const fetchDepartments = async () => {
    if (!companyId) {
      setDepartmentsLoaded(false);
      return [];
    }
    
    try {
      console.log('StaffContext: Fetching departments for company:', companyId);
      
      const departmentsCollectionRef = collection(db, 'companies', companyId, 'departments');
      const snapshot = await getDocs(departmentsCollectionRef);
      
      const departmentsList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || `Department ${doc.id.substring(0, 8)}`,
        ...doc.data()
      }));
      
      console.log('StaffContext: Departments loaded:', departmentsList);
      setDepartments(departmentsList);
      setDepartmentsLoaded(true);
      clearError();
      
      return departmentsList;
    } catch (err) {
      handleError(err, 'load departments');
      setDepartmentsLoaded(false);
      return [];
    }
  };
  
  // Fetch all staff members for the company
  const fetchAllStaff = async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      console.log('StaffContext: Fetching staff for company:', companyId);
      
      const staffCollectionRef = collection(db, 'companies', companyId, 'staff');
      const snapshot = await getDocs(staffCollectionRef);
      
      // Get departments if not already loaded
      let depts = departments;
      if (!departmentsLoaded || depts.length === 0) {
        depts = await fetchDepartments();
      }
      
      const staffList = snapshot.docs.map(doc => {
        const staffMember = Staff.fromFirestore(doc);
        // Set department names
        staffMember.setDepartmentNames(depts);
        return staffMember;
      });
      
      console.log('StaffContext: Staff loaded:', staffList.length, 'members');
      setStaff(staffList);
      clearError();
    } catch (err) {
      handleError(err, 'load staff members');
    } finally {
      setLoading(false);
    }
  };

  // Fetch a single staff member by ID
  const fetchStaffMember = async (staffId) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }
    
    try {
      console.log('StaffContext: Fetching staff member:', staffId);
      
      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      const docSnap = await getDoc(staffRef);
      
      if (docSnap.exists()) {
        const staffMember = Staff.fromFirestore(docSnap);
        
        // Get departments if not already loaded
        let depts = departments;
        if (!departmentsLoaded || depts.length === 0) {
          console.log('StaffContext: Departments not loaded, fetching...');
          depts = await fetchDepartments();
        }
        
        // Set department names
        staffMember.setDepartmentNames(depts);
        console.log('StaffContext: Staff member loaded with departments:', staffMember);
        
        return staffMember;
      } else {
        throw new Error('Staff member not found');
      }
    } catch (err) {
      console.error('StaffContext: Error fetching staff member:', err);
      throw err;
    }
  };

  // Create a new staff member with secure PIN
  const createStaffMember = async (staffData) => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }
    
    setOperationLoading(prev => ({ ...prev, creating: true }));
    
    try {
      const staffCollectionRef = collection(db, 'companies', companyId, 'staff');
      const newStaffRef = doc(staffCollectionRef);
      
      // Prepare base staff data without PIN
      const baseStaffData = {
        id: newStaffRef.id,
        name: staffData.name,
        departmentIds: staffData.departmentIds || [],
        primaryDepartmentId: staffData.primaryDepartmentId || '',
        email: staffData.email || '',
        role: staffData.role || 'staff',
        employmentInfo: staffData.employmentInfo || new StaffEmploymentInfo()
      };
      
      // Handle PIN creation with secure hashing
      let pinData = {};
      if (staffData.pin && PINHashingService.isValidPIN(staffData.pin)) {
        const { hash, salt } = await PINHashingService.hashPIN(staffData.pin);
        pinData = {
          pinHash: hash,
          pinSalt: salt,
          pinVersion: 'v2',
          pinCreatedAt: new Date()
        };
        console.log('✅ PIN hashed successfully for new staff member');
      } else {
        // Default PIN if none provided
        const { hash, salt } = await PINHashingService.hashPIN('0000');
        pinData = {
          pinHash: hash,
          pinSalt: salt,
          pinVersion: 'v2',
          pinCreatedAt: new Date()
        };
        console.log('⚠️ Using default PIN (0000) for new staff member');
      }
      
      const newStaff = new Staff(
        baseStaffData.id,
        baseStaffData.name,
        null, // No plain text PIN
        baseStaffData.departmentIds,
        baseStaffData.primaryDepartmentId,
        baseStaffData.email,
        baseStaffData.role,
        baseStaffData.employmentInfo
        // active/archivedAt/archivedBy default to true/null/null - correct for a new hire
      );
      
      // Combine base data with secure PIN data
      const firestoreData = {
        ...newStaff.toFirestore(),
        ...pinData
      };
      
      await setDoc(newStaffRef, firestoreData);
      console.log('✅ Staff member created successfully in database');
      
      // Set department names
      newStaff.setDepartmentNames(departments);
      
      // Update local state
      setStaff(prevStaff => [...prevStaff, newStaff]);
      clearError();
      
      return newStaff;
    } catch (err) {
      const errorMessage = handleError(err, 'create staff member');
      throw new Error(errorMessage);
    } finally {
      setOperationLoading(prev => ({ ...prev, creating: false }));
    }
  };

  // Enhanced update staff member with better error handling and optimistic updates
  const updateStaffMember = async (staffId, staffData) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }
    
    setOperationLoading(prev => ({ ...prev, updating: true }));
    
    // Store original staff data for rollback if needed
    const originalStaff = staff.find(s => s.id === staffId);
    
    try {
      console.log('StaffContext: Updating staff member:', { staffId, staffData });
      
      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      
      // Convert staffData to a plain object if it's a Staff instance
      const dataToUpdate = staffData.toFirestore ? staffData.toFirestore() : { ...staffData };
      
      // Handle PIN update securely
      if (staffData.pin && PINHashingService.isValidPIN(staffData.pin)) {
        try {
          // Hash the new PIN and get the secure fields
          const { hash, salt } = await PINHashingService.hashPIN(staffData.pin);
          
          // Add secure PIN fields to update data
          dataToUpdate.pinHash = hash;
          dataToUpdate.pinSalt = salt;
          dataToUpdate.pinVersion = 'v2';
          dataToUpdate.pinCreatedAt = new Date();
          dataToUpdate.pin = deleteField(); // Remove old plain text PIN
          
          console.log('✅ PIN will be updated with secure hashing for staff:', staffId);
        } catch (pinError) {
          console.error('Error hashing PIN:', pinError);
          throw new Error('Failed to process PIN update');
        }
      } else {
        // Remove PIN from update if it's empty or invalid
        delete dataToUpdate.pin;
      }
      
      console.log('Final data to update:', dataToUpdate);
      
      // Optimistic update - update local state first
      if (originalStaff) {
        const optimisticStaff = { ...originalStaff, ...staffData };
        setStaff(prevStaff => 
          prevStaff.map(s => s.id === staffId ? optimisticStaff : s)
        );
      }
      
      // Update database
      if (Object.keys(dataToUpdate).length > 0) {
        await updateDoc(staffRef, dataToUpdate);
        console.log('✅ Staff member updated successfully in database');
      }
      
      // Get the updated staff member from database to ensure consistency
      const updatedStaff = await fetchStaffMember(staffId);
      
      // Update local state with database version
      setStaff(prevStaff => 
        prevStaff.map(s => s.id === staffId ? updatedStaff : s)
      );
      
      clearError();
      return updatedStaff;
    } catch (err) {
      // Rollback optimistic update on error
      if (originalStaff) {
        setStaff(prevStaff => 
          prevStaff.map(s => s.id === staffId ? originalStaff : s)
        );
      }
      
      const errorMessage = handleError(err, 'update staff member');
      throw new Error(errorMessage);
    } finally {
      setOperationLoading(prev => ({ ...prev, updating: false }));
    }
  };

  // Update staff employment info (startDate / vacationDays only).
  // NOTE: contractHours / workSchedule are legacy fields no longer collected
  // in the UI - Schedule Planner shifts are the single source of truth for
  // Hours Account, sick leave, and vacation calculations. This function is
  // kept for potential future employmentInfo updates (e.g. vacationDays),
  // but no longer defaults, validates, or persists contractHours/workSchedule.
  const updateStaffEmploymentInfo = async (staffId, employmentInfo) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }
    
    setOperationLoading(prev => ({ ...prev, updating: true }));
    
    // Store original staff data for rollback
    const originalStaff = staff.find(s => s.id === staffId);
    
    try {
      console.log('StaffContext: Updating staff employment info:', { staffId, employmentInfo });
      
      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      
      // Ensure we have a proper StaffEmploymentInfo instance
      let employmentInfoToSave = employmentInfo;
      if (!(employmentInfo instanceof StaffEmploymentInfo)) {
        // Create a proper instance if it's a plain object
        // (contractHours/workSchedule no longer collected - not passed here)
        employmentInfoToSave = new StaffEmploymentInfo(
          employmentInfo.startDate || new Date(),
          null,
          employmentInfo.vacationDays || {},
          null
        );
      }
      
      // Optimistic update
      if (originalStaff) {
        const optimisticStaff = new Staff(
          originalStaff.id,
          originalStaff.name,
          null,
          originalStaff.departmentIds || [],
          originalStaff.primaryDepartmentId || '',
          originalStaff.email || '',
          originalStaff.role || 'staff',
          employmentInfoToSave,
          originalStaff.active,
          originalStaff.archivedAt,
          originalStaff.archivedBy
        );
        optimisticStaff.setDepartmentNames(departments);
        
        setStaff(prevStaff => 
          prevStaff.map(s => s.id === staffId ? optimisticStaff : s)
        );
      }
      
      // Update database
      await updateDoc(staffRef, { 
        employmentInfo: employmentInfoToSave.toFirestore() 
      });
      
      console.log('✅ Successfully updated employment info in database');
      clearError();
      
      return employmentInfoToSave;
    } catch (err) {
      // Rollback optimistic update on error
      if (originalStaff) {
        setStaff(prevStaff => 
          prevStaff.map(s => s.id === staffId ? originalStaff : s)
        );
      }
      
      const errorMessage = handleError(err, 'update employment information');
      throw new Error(errorMessage);
    } finally {
      setOperationLoading(prev => ({ ...prev, updating: false }));
    }
  };

  // Enhanced delete staff member with confirmation
  // NOTE: Kept as a hard delete for the "Endgültig löschen" action in the
  // archived-staff view only. Deleting the staff doc does NOT delete
  // timeEntries/schedules/vacations/sickLeave (separate paths), but it does
  // make that staff member's history unreachable from the UI.
  const deleteStaffMember = async (staffId) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }
    
    setOperationLoading(prev => ({ ...prev, deleting: true }));
    
    // Store staff data for potential rollback
    const staffToDelete = staff.find(s => s.id === staffId);
    
    try {
      // Optimistic removal from local state
      setStaff(prevStaff => prevStaff.filter(s => s.id !== staffId));
      
      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      await deleteDoc(staffRef);
      
      console.log('✅ Staff member deleted successfully from database');
      clearError();
      
      return { success: true, id: staffId };
    } catch (err) {
      // Rollback optimistic removal on error
      if (staffToDelete) {
        setStaff(prevStaff => [...prevStaff, staffToDelete]);
      }
      
      const errorMessage = handleError(err, 'delete staff member');
      throw new Error(errorMessage);
    } finally {
      setOperationLoading(prev => ({ ...prev, deleting: false }));
    }
  };

  // Archive a staff member: sets active:false and clears all PIN fields.
  //
  // Clearing the PIN is not cosmetic - it's what makes an archived staff
  // member's PIN immediately reusable by an active colleague on the
  // existing, unmodified iOS app: PINManager.verifyStaffPIN returns
  // "no_pin_found" for a doc with no pinHash/pinSalt/pin, and
  // StaffLoginView.authenticateAgainstStaff treats that as "skip" and
  // advances to the next candidate.
  const archiveStaffMember = async (staffId) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }

    setOperationLoading(prev => ({ ...prev, archiving: true }));

    try {
      // Guard: refuse to archive while the person is clocked in.
      const activeSessionRef = doc(db, 'companies', companyId, 'activeSessions', staffId);
      const activeSessionSnap = await getDoc(activeSessionRef);
      if (activeSessionSnap.exists()) {
        // Identifiable error code - UI layer maps this to the translated
        // string staff.cannotArchiveWhileClockedIn rather than us setting
        // a hardcoded/localized message here in the context.
        throw new Error('CANNOT_ARCHIVE_WHILE_CLOCKED_IN');
      }

      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      await updateDoc(staffRef, {
        active: false,
        archivedAt: serverTimestamp(),
        archivedBy: currentUser?.uid || null,
        pin: deleteField(),
        pinHash: deleteField(),
        pinSalt: deleteField(),
        pinVersion: deleteField()
      });

      console.log('✅ Staff member archived successfully:', staffId);

      // Refetch so local state reflects the resolved server timestamp
      // and the cleared PIN fields.
      const updatedStaff = await fetchStaffMember(staffId);
      setStaff(prevStaff =>
        prevStaff.map(s => s.id === staffId ? updatedStaff : s)
      );

      clearError();
      return updatedStaff;
    } catch (err) {
      if (err.message === 'CANNOT_ARCHIVE_WHILE_CLOCKED_IN') {
        throw err;
      }

      const errorMessage = handleError(err, 'archive staff member');
      throw new Error(errorMessage);
    } finally {
      setOperationLoading(prev => ({ ...prev, archiving: false }));
    }
  };

  // Reactivate a previously archived staff member. A new PIN is mandatory
  // because archiving destroyed the old one - there is nothing to restore.
  // Runs the same duplicate-PIN check as create/edit before writing.
  const reactivateStaffMember = async (staffId, newPin) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }

    if (!newPin || !PINHashingService.isValidPIN(newPin)) {
      throw new Error('A valid 4-digit PIN is required to reactivate a staff member');
    }

    setOperationLoading(prev => ({ ...prev, updating: true }));

    try {
      const staffMember = staff.find(s => s.id === staffId);
      if (!staffMember) {
        throw new Error('Staff member not found');
      }

      const conflict = await isPinTakenInDepartments(
        newPin,
        staffMember.departmentIds || [],
        staffId
      );
      if (conflict) {
        // Identifiable error - UI layer reads err.conflict.name to build
        // the translated staff.pinTakenInDepartment message.
        const pinTakenError = new Error('PIN_TAKEN');
        pinTakenError.conflict = conflict;
        throw pinTakenError;
      }

      const { hash, salt } = await PINHashingService.hashPIN(newPin);

      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      await updateDoc(staffRef, {
        active: true,
        archivedAt: deleteField(),
        archivedBy: deleteField(),
        pinHash: hash,
        pinSalt: salt,
        pinVersion: 'v2',
        pinCreatedAt: new Date()
      });

      console.log('✅ Staff member reactivated successfully:', staffId);

      const updatedStaff = await fetchStaffMember(staffId);
      setStaff(prevStaff =>
        prevStaff.map(s => s.id === staffId ? updatedStaff : s)
      );

      clearError();
      return updatedStaff;
    } catch (err) {
      if (err.message === 'PIN_TAKEN') {
        throw err;
      }

      const errorMessage = handleError(err, 'reactivate staff member');
      throw new Error(errorMessage);
    } finally {
      setOperationLoading(prev => ({ ...prev, updating: false }));
    }
  };

  // Check whether a PIN is already in use by an ACTIVE colleague sharing at
  // least one department with the given departmentIds.
  //
  // Deliberately queries Firestore directly rather than using the cached
  // `staff` state: Staff.fromFirestore() does not read pinHash/pinSalt at
  // all, and defaults `pin` to '0000' for any staff member using the secure
  // hashed format - so the cached state cannot be used for this check.
  //
  // PINs are stored as a per-staff salted hash, so hashes cannot be compared
  // to each other directly; each candidate must be verified individually
  // against its own salt.
  const isPinTakenInDepartments = async (pin, departmentIds, excludeStaffId = null) => {
    if (!companyId || !departmentIds || departmentIds.length === 0) {
      return null;
    }

    try {
      // 'array-contains-any' supports a maximum of 10 values.
      const deptsToQuery = departmentIds.slice(0, 10);

      const staffCollectionRef = collection(db, 'companies', companyId, 'staff');
      const q = query(
        staffCollectionRef,
        where('departmentIds', 'array-contains-any', deptsToQuery)
      );
      const snapshot = await getDocs(q);

      for (const docSnap of snapshot.docs) {
        if (docSnap.id === excludeStaffId) continue;

        const data = docSnap.data();

        // Archived colleagues don't block a PIN - their fields are cleared
        // on archive, but skip explicitly in case of legacy/edge-case data.
        if (data.active === false) continue;

        if (data.pinHash && data.pinSalt) {
          const isMatch = await PINHashingService.verifyPIN(pin, data.pinHash, data.pinSalt);
          if (isMatch) {
            return { id: docSnap.id, name: data.name || 'Unknown' };
          }
        } else if (data.pin && data.pin === pin) {
          // Legacy plaintext PIN
          return { id: docSnap.id, name: data.name || 'Unknown' };
        }
      }

      return null;
    } catch (err) {
      console.error('StaffContext: Error checking PIN uniqueness:', err);
      throw err;
    }
  };

  // Verify staff PIN (for authentication)
  const verifyStaffPIN = async (staffId, enteredPIN) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }
    
    try {
      const result = await PINHashingService.verifyStaffPIN(companyId, staffId, enteredPIN);
      
      // If PIN is valid but needs migration, migrate it automatically
      if (result.isValid && result.needsMigration) {
        console.log('🔄 Migrating legacy PIN for staff:', staffId);
        await PINHashingService.migrateLegacyPIN(companyId, staffId, enteredPIN);
      }
      
      return result.isValid;
    } catch (err) {
      console.error('Error verifying staff PIN:', err);
      throw err;
    }
  };

  // Fetch department default schedule
  const fetchDepartmentDefaultSchedule = async (departmentId) => {
    if (!companyId || !departmentId) {
      throw new Error('Company ID and Department ID are required');
    }
    
    try {
      const departmentRef = doc(db, 'companies', companyId, 'departments', departmentId);
      const docSnap = await getDoc(departmentRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        if (data.defaultWorkSchedule) {
          const workSchedule = {};
          
          Object.keys(data.defaultWorkSchedule).forEach(day => {
            const dayData = data.defaultWorkSchedule[day];
            workSchedule[day] = new WorkScheduleDay(
              dayData.isWorkDay,
              dayData.hours
            );
          });
          
          return workSchedule;
        }
      }
      
      return null;
    } catch (err) {
      console.error('Error fetching department default schedule:', err);
      throw err;
    }
  };

  // Enhanced schedule exceptions management
  const fetchScheduleExceptions = async (staffId) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }
    
    try {
      const exceptionsCollectionRef = collection(
        db, 
        'companies', 
        companyId, 
        'staff', 
        staffId, 
        'scheduleExceptions'
      );
      
      const snapshot = await getDocs(exceptionsCollectionRef);
      
      const exceptions = snapshot.docs.map(doc => 
        ScheduleException.fromFirestore(doc.data(), doc.id)
      );
      
      console.log(`✅ Fetched ${exceptions.length} schedule exceptions for staff ${staffId}`);
      return exceptions;
    } catch (err) {
      handleError(err, 'fetch schedule exceptions');
      throw err;
    }
  };

  const addScheduleException = async (staffId, exception) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }
    
    try {
      const exceptionRef = doc(
        db, 
        'companies', 
        companyId, 
        'staff', 
        staffId, 
        'scheduleExceptions',
        exception.id
      );
      
      await setDoc(exceptionRef, exception.toFirestore());
      console.log('✅ Schedule exception added successfully');
      
      return exception;
    } catch (err) {
      handleError(err, 'add schedule exception');
      throw err;
    }
  };

  const deleteScheduleException = async (staffId, exceptionId) => {
    if (!companyId || !staffId || !exceptionId) {
      throw new Error('Company ID, Staff ID, and Exception ID are required');
    }
    
    try {
      const exceptionRef = doc(
        db, 
        'companies', 
        companyId, 
        'staff', 
        staffId, 
        'scheduleExceptions',
        exceptionId
      );
      
      await deleteDoc(exceptionRef);
      console.log('✅ Schedule exception deleted successfully');
      
      return { success: true, id: exceptionId };
    } catch (err) {
      handleError(err, 'delete schedule exception');
      throw err;
    }
  };

  // ========== TIME MANAGEMENT FUNCTIONS ==========

  // Get active session for a staff member
  const getActiveSession = async (staffId) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }

    try {
      console.log('StaffContext: Fetching active session for staff:', staffId);
      
      const activeSessionRef = doc(db, 'companies', companyId, 'activeSessions', staffId);
      const docSnap = await getDoc(activeSessionRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: staffId,
          departmentId: data.departmentId || '',
          clockIn: data.clockIn,
          onBreak: data.onBreak || false,
          breakStart: data.breakStart || null,
          deviceType: data.deviceType || 'web',
          deviceId: data.deviceId || 'unknown',
          location: data.location || { lat: 0, lon: 0 }
        };
      }
      
      return null;
    } catch (err) {
      console.error('StaffContext: Error fetching active session:', err);
      throw err;
    }
  };

  // Get time entries for a staff member
  const getTimeEntries = async (staffId, startDate = null, endDate = null, limitResults = 10) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }

    try {
      console.log('StaffContext: Fetching time entries for staff:', staffId);
      
      const timeEntriesRef = collection(db, 'companies', companyId, 'timeEntries', staffId, 'entries');
      let q = query(timeEntriesRef, orderBy('date', 'desc'));
      
      if (limitResults) {
        q = query(q, limit(limitResults));
      }
      
      const snapshot = await getDocs(q);
      
      const timeEntries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        clockIns: doc.data().clockIns || [],
        clockOuts: doc.data().clockOuts || [],
        breakStarts: doc.data().breakStarts || [],
        breakEnds: doc.data().breakEnds || []
      }));
      
      console.log(`StaffContext: Fetched ${timeEntries.length} time entries for staff ${staffId}`);
      return timeEntries;
    } catch (err) {
      console.error('StaffContext: Error fetching time entries:', err);
      throw err;
    }
  };

  // Admin clock out - force clock out with optional time adjustments
  const adminClockOut = async (staffId, adjustments = {}) => {
    if (!companyId || !staffId) {
      throw new Error('Company ID and Staff ID are required');
    }

    setOperationLoading(prev => ({ ...prev, clockingOut: true }));
    
    try {
      console.log('StaffContext: Admin clock out for staff:', staffId, adjustments);
      
      // Get active session first
      const activeSession = await getActiveSession(staffId);
      if (!activeSession) {
        throw new Error('No active session found for this staff member');
      }
      
      const timestamp = adjustments.clockOutTime ? Timestamp.fromDate(new Date(adjustments.clockOutTime)) : Timestamp.now();
      
      // Calculate durations
      const clockInTime = activeSession.clockIn.toDate();
      const clockOutTime = timestamp.toDate();
      const totalSessionDuration = (clockOutTime.getTime() - clockInTime.getTime()) / 1000;
      
      let totalBreakDuration = 0;
      if (activeSession.onBreak && activeSession.breakStart) {
        const breakStartTime = activeSession.breakStart.toDate();
        totalBreakDuration = (clockOutTime.getTime() - breakStartTime.getTime()) / 1000;
      }
      
      const workDuration = Math.max(0, totalSessionDuration - totalBreakDuration);
      
      // Delete active session
      const activeSessionRef = doc(db, 'companies', companyId, 'activeSessions', staffId);
      await deleteDoc(activeSessionRef);
      
      // Find or create time entry for today
      const dateFormatter = new Intl.DateTimeFormat('en-CA'); // YYYY-MM-DD format
      const dateString = dateFormatter.format(clockOutTime);
      
      const timeEntriesRef = collection(db, 'companies', companyId, 'timeEntries', staffId, 'entries');
      const existingEntryQuery = query(timeEntriesRef, where('date', '==', dateString));
      const existingSnapshot = await getDocs(existingEntryQuery);
      
      if (existingSnapshot.docs.length > 0) {
        // Update existing entry
        const entryDoc = existingSnapshot.docs[0];
        const entryRef = doc(db, 'companies', companyId, 'timeEntries', staffId, 'entries', entryDoc.id);
        const currentData = entryDoc.data();
        
        const currentWorkDuration = currentData.totalWorkDuration || 0;
        const currentBreakDuration = currentData.totalBreakDuration || 0;
        
        await updateDoc(entryRef, {
          clockOuts: FieldValue.arrayUnion(timestamp),
          totalWorkDuration: currentWorkDuration + workDuration,
          totalBreakDuration: currentBreakDuration + totalBreakDuration,
          sessionStatus: 'Clocked Out'
        });
        
        console.log('✅ Updated existing time entry with clock out');
      } else {
        // Create new entry
        const newEntryRef = doc(timeEntriesRef);
        const timeEntryData = {
          departmentId: activeSession.departmentId,
          date: dateString,
          clockIns: [activeSession.clockIn],
          clockOuts: [timestamp],
          breakStarts: activeSession.breakStart ? [activeSession.breakStart] : [],
          breakEnds: [], // No break end since we're clocking out during break
          totalWorkDuration: workDuration,
          totalBreakDuration: totalBreakDuration,
          sessionStatus: 'Clocked Out',
          deviceType: 'web-admin',
          deviceId: 'admin-override',
          note: adjustments.note || 'Admin clock out'
        };
        
        await setDoc(newEntryRef, timeEntryData);
        console.log('✅ Created new time entry with admin clock out');
      }
      
      clearError();
      return { 
        success: true, 
        workDuration, 
        totalBreakDuration,
        clockOutTime: timestamp.toDate()
      };
    } catch (err) {
      const errorMessage = handleError(err, 'admin clock out');
      throw new Error(errorMessage);
    } finally {
      setOperationLoading(prev => ({ ...prev, clockingOut: false }));
    }
  };

  // Update time entry with new timestamps
  const updateTimeEntry = async (staffId, entryId, newTimestamps) => {
    if (!companyId || !staffId || !entryId) {
      throw new Error('Company ID, Staff ID, and Entry ID are required');
    }

    setOperationLoading(prev => ({ ...prev, updatingTimeEntry: true }));
    
    try {
      console.log('StaffContext: Updating time entry:', { staffId, entryId, newTimestamps });
      
      const entryRef = doc(db, 'companies', companyId, 'timeEntries', staffId, 'entries', entryId);
      
      // Calculate new durations
      const durations = recalculateDurations(
        newTimestamps.clockIns || [],
        newTimestamps.clockOuts || [],
        newTimestamps.breakStarts || [],
        newTimestamps.breakEnds || []
      );
      
      const updateData = {
        ...newTimestamps,
        totalWorkDuration: durations.workDuration,
        totalBreakDuration: durations.breakDuration,
        lastModified: Timestamp.now(),
        modifiedBy: 'admin'
      };
      
      await updateDoc(entryRef, updateData);
      
      console.log('✅ Time entry updated successfully');
      clearError();
      
      return { success: true, durations };
    } catch (err) {
      const errorMessage = handleError(err, 'update time entry');
      throw new Error(errorMessage);
    } finally {
      setOperationLoading(prev => ({ ...prev, updatingTimeEntry: false }));
    }
  };

  // Recalculate work and break durations from timestamps
  const recalculateDurations = (clockIns, clockOuts, breakStarts, breakEnds) => {
    try {
      let totalWorkDuration = 0;
      let totalBreakDuration = 0;
      
      // Calculate work duration from clock in/out pairs
      const workSessions = Math.min(clockIns.length, clockOuts.length);
      for (let i = 0; i < workSessions; i++) {
        const clockInTime = clockIns[i].toDate ? clockIns[i].toDate() : new Date(clockIns[i]);
        const clockOutTime = clockOuts[i].toDate ? clockOuts[i].toDate() : new Date(clockOuts[i]);
        const sessionDuration = (clockOutTime.getTime() - clockInTime.getTime()) / 1000;
        totalWorkDuration += Math.max(0, sessionDuration);
      }
      
      // Calculate break duration from break start/end pairs
      const breakSessions = Math.min(breakStarts.length, breakEnds.length);
      for (let i = 0; i < breakSessions; i++) {
        const breakStartTime = breakStarts[i].toDate ? breakStarts[i].toDate() : new Date(breakStarts[i]);
        const breakEndTime = breakEnds[i].toDate ? breakEnds[i].toDate() : new Date(breakEnds[i]);
        const breakDuration = (breakEndTime.getTime() - breakStartTime.getTime()) / 1000;
        totalBreakDuration += Math.max(0, breakDuration);
      }
      
      // Subtract break time from work time
      const finalWorkDuration = Math.max(0, totalWorkDuration - totalBreakDuration);
      
      console.log('Duration calculation:', {
        rawWorkDuration: totalWorkDuration,
        breakDuration: totalBreakDuration,
        finalWorkDuration
      });
      
      return {
        workDuration: finalWorkDuration,
        breakDuration: totalBreakDuration
      };
    } catch (err) {
      console.error('Error calculating durations:', err);
      return { workDuration: 0, breakDuration: 0 };
    }
  };

  // Enhanced search staff with better filtering
  const searchStaff = async (searchTerm, departmentId = null) => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }
    
    try {
      let filteredStaff = [...staff];
      
      if (searchTerm) {
        const term = searchTerm.toLowerCase().trim();
        filteredStaff = filteredStaff.filter(s => 
          s.name.toLowerCase().includes(term) || 
          (s.email && s.email.toLowerCase().includes(term)) ||
          (s.role && s.role.toLowerCase().includes(term))
        );
      }
      
      if (departmentId) {
        filteredStaff = filteredStaff.filter(s => 
          s.departmentIds.includes(departmentId)
        );
      }
      
      console.log(`🔍 Search results: ${filteredStaff.length} staff members found`);
      return filteredStaff;
    } catch (err) {
      handleError(err, 'search staff');
      throw err;
    }
  };

  // Get staff by department
  const getStaffByDepartment = (departmentId) => {
    if (!departmentId) return [];
    
    const departmentStaff = staff.filter(s => s.departmentIds.includes(departmentId));
    console.log(`📋 Found ${departmentStaff.length} staff members in department ${departmentId}`);
    return departmentStaff;
  };

  // Get department by ID - Enhanced with better error handling
  const getDepartmentById = (departmentId) => {
    if (!departmentId) return null;
    
    const department = departments.find(d => d.id === departmentId);
    if (!department) {
      console.warn(`StaffContext: Department not found for ID: ${departmentId}`);
      return null;
    }
    
    return department;
  };

  // Get department name by ID - Enhanced with better fallback
  const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'Unassigned';
    
    const dept = getDepartmentById(departmentId);
    if (dept && dept.name) {
      return dept.name;
    }
    
    // Fallback: if department exists but no name, use a formatted ID
    if (dept) {
      return `Department ${departmentId.substring(0, 8)}`;
    }
    
    // Final fallback: department not found
    console.warn(`StaffContext: Department name not found for ID: ${departmentId}`);
    return `Unknown (${departmentId.substring(0, 8)})`;
  };

  // Refresh department names for all staff - Enhanced to maintain Staff instances
  const refreshDepartmentNames = () => {
    setStaff(prevStaff => 
      prevStaff.map(staffMember => {
        // Create a new Staff instance
        const updatedStaff = new Staff(
          staffMember.id,
          staffMember.name,
          null, // No plain text PIN
          staffMember.departmentIds || [],
          staffMember.primaryDepartmentId || '',
          staffMember.email || '',
          staffMember.role || 'staff',
          staffMember.employmentInfo,
          staffMember.active,
          staffMember.archivedAt,
          staffMember.archivedBy
        );
        updatedStaff.setDepartmentNames(departments);
        return updatedStaff;
      })
    );
    console.log('🔄 Department names refreshed for all staff members');
  };

  // Enhanced refresh departments function
  const refreshDepartments = async () => {
    try {
      const depts = await fetchDepartments();
      // Update all staff with new department names
      refreshDepartmentNames();
      console.log('✅ Departments and staff department names refreshed');
      return depts;
    } catch (err) {
      handleError(err, 'refresh departments');
      throw err;
    }
  };

  // New helper function: Check if any operations are in progress
  const isAnyOperationLoading = () => {
    return Object.values(operationLoading).some(loading => loading);
  };

  // New helper function: Get current operation status
  const getOperationStatus = () => {
    return operationLoading;
  };

  const value = {
    // Data
    staff,
    staffMembers: staff, // Alias for backward compatibility
    activeStaff,
    archivedStaff,
    departments,
    
    // Loading states
    loading,
    operationLoading,
    isAnyOperationLoading,
    getOperationStatus,
    
    // Error handling
    error,
    clearError,
    
    // Status flags
    departmentsLoaded,
    
    // Staff operations
    fetchAllStaff,
    fetchStaffMember,
    createStaffMember,
    updateStaffMember,
    updateStaffEmploymentInfo,
    deleteStaffMember,
    archiveStaffMember,
    reactivateStaffMember,
    isPinTakenInDepartments,
    verifyStaffPIN,
    
    // Department operations
    fetchDepartments,
    refreshDepartments,
    getDepartmentById,
    getDepartmentName,
    refreshDepartmentNames,
    fetchDepartmentDefaultSchedule,
    
    // Schedule exceptions
    fetchScheduleExceptions,
    addScheduleException,
    deleteScheduleException,
    
    // Time management operations
    getActiveSession,
    getTimeEntries,
    adminClockOut,
    updateTimeEntry,
    recalculateDurations,
    
    // Search and filtering
    searchStaff,
    getStaffByDepartment
  };

  return (
    <StaffContext.Provider value={value}>
      {children}
    </StaffContext.Provider>
  );
};

export default StaffContext;